import React from "react";
import styled from "styled-components";
import ReactMarkdown from "react-markdown";

// Styled Components for Citations
const CitationsContainer = styled.div`
  margin-top: 0.75rem;
  padding: 0.5rem;
  background: #f8f9fa;
  border: 0.125rem solid #e9ecef;
  border-radius: 0.5rem;
  font-size: 0.875rem;
`;

const CitationsTitle = styled.div`
  font-size: 0.875rem;
  opacity: 0.7;
  display: block;
  color: #666;
  white-space: nowrap;
  width: fit-content;
  margin-bottom: 0.5rem;
`;

const CitationsLoading = styled(CitationsTitle)`
  margin-bottom: 0;
`;

const CitationItem = styled.div`
  margin-bottom: 0.75rem;
  padding: 0.5rem;
  background: white;
  border: 0.125rem solid #dee2e6;
  border-radius: 0.25rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const CitationHeader = styled.div`
  font-weight: 500;
  color: #000000;
  margin-bottom: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
  flex-direction: row;
`;

const CitationId = styled.div`
  font-weight: 500;
  font-size: 0.75rem;
  color: #ffffff;
  background-color: rgba(0, 0, 0, 0.5);
  border-radius: 100%;
  width: 1.25rem;
  height: 1.25rem;
  min-width: 1.25rem;
  min-height: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const InlineCitationId = styled.span`
  font-weight: 500;
  font-size: 0.75rem;
  color: transparent;
  background-color: rgba(0, 0, 0, 0.5);
  border-radius: 100%;
  width: 1.25rem;
  height: 1.25rem;
  min-width: 1.25rem;
  min-height: 1.25rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;
  cursor: default;
  opacity: 0.25;
  vertical-align: 2px;

  ${({ $areCitationsReady }) =>
    $areCitationsReady &&
    `
      cursor: pointer;
      opacity: 1;
      color: #ffffff;

      &:hover {
        background-color: rgba(0, 0, 0, 0.75);
      }
    `}
`;

const CitationSource = styled.div`
  font-weight: 500;
  color: #000000;
`;

const CitationContent = styled.div`
  color: #6c757d;
  line-height: 1.4;
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;

const CitationMetadata = styled.div`
  font-size: 0.875rem;
  color: #6c757d;
  margin-top: 0.5rem;
  opacity: 0.8;
`;

// Helper function to process message content and render inline citations
export const processMessageContent = (
  content,
  citations = [],
  responseId = null,
  citationsLoading = false,
  modelname = null
) => {
  if (!content) return null;

  // Function to handle clicking on inline citations
  const handleInlineCitationClick = (citationNumber) => {
    if (
      responseId &&
      modelname &&
      !citationsLoading &&
      typeof citationNumber === "number"
    ) {
      const citationElement = document.getElementById(
        `citation-${responseId}-${modelname}-${citationNumber}`
      );
      if (citationElement) {
        citationElement.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  };

  // Check if citations are ready (not loading and have citations)
  const areCitationsReady = !citationsLoading && citations.length > 0;

  // Helper function to process citations in text content
  const processCitationsInText = (child) => {
    if (typeof child !== "string") {
      return child;
    }

    // Pattern to match citations like [citations-1], [citation-1], [citations-10], etc.
    const citationPattern = /\[citations?-(\d+)\]/gi;

    // Find all citation matches first
    const matches = [...child.matchAll(citationPattern)];

    // If no citations found, return text as-is
    if (matches.length === 0) {
      return child;
    }

    const elements = [];
    let lastIndex = 0;

    // Process each citation match
    matches.forEach((match, index) => {
      const [fullMatch, citationNumber] = match;
      const matchIndex = match.index;

      // Add text before this citation
      if (matchIndex > lastIndex) {
        const textBefore = child.substring(lastIndex, matchIndex);
        if (textBefore) {
          elements.push(textBefore);
        }
      }

      // Determine what to display for this citation
      let displayContent, displayNumber, clickHandler, titleText;

      if (areCitationsReady) {
        // Show citation number
        displayNumber = parseInt(citationNumber, 10);
        displayContent = displayNumber;
        clickHandler = () => handleInlineCitationClick(displayNumber);
        titleText = `Go to source ${displayNumber}`;
      } else {
        // Show "?" while loading or citations not ready
        displayContent = "?";
        displayNumber = "?";
        clickHandler = undefined; // Disable clicking while loading
        titleText = "Loading source...";
      }

      // Add the citation element
      elements.push(
        <InlineCitationId
          key={`cite-${index}`}
          id={`inline-citation-${responseId}-${modelname}-${displayNumber}`}
          onClick={clickHandler}
          title={titleText}
          $areCitationsReady={areCitationsReady}
        >
          {displayContent}
        </InlineCitationId>
      );

      // Update lastIndex to after this match
      lastIndex = matchIndex + fullMatch.length;
    });

    // Add any remaining text after the last citation
    if (lastIndex < child.length) {
      const textAfter = child.substring(lastIndex);
      if (textAfter) {
        elements.push(textAfter);
      }
    }

    return elements;
  };

  // Custom components to handle citations within paragraphs and list items
  const customComponents = {
    p: ({ children, ...props }) => {
      // Process paragraph content to handle citations
      const processedChildren = React.Children.map(children, (child) => {
        return processCitationsInText(child);
      });

      return <p {...props}>{processedChildren}</p>;
    },
    li: ({ children, ...props }) => {
      // Process list item content to handle citations
      const processedChildren = React.Children.map(children, (child) => {
        return processCitationsInText(child);
      });

      return <li {...props}>{processedChildren}</li>;
    }
  };

  return (
    <ReactMarkdown
      components={customComponents}
      remarkPlugins={[]}
      rehypePlugins={[]}
      skipHtml={false}
    >
      {content.replace(/\n/g, "  \n")}
    </ReactMarkdown>
  );
};

// Citations display component
export const Citations = ({
  citations,
  citationsLoading,
  responseId,
  modelname
}) => {
  if (!((citations && citations.length > 0) || citationsLoading)) {
    return null;
  }

  return (
    <CitationsContainer>
      {citationsLoading && (!citations || citations.length === 0) ? (
        <CitationsLoading>Loading sources...</CitationsLoading>
      ) : (
        <>
          <CitationsTitle>Sources</CitationsTitle>
          {citations
            ?.slice()
            .sort((a, b) => {
              const aMatch = a.id?.match(/(\d+)/);
              const bMatch = b.id?.match(/(\d+)/);
              const aNum = aMatch ? parseInt(aMatch[1], 10) : 0;
              const bNum = bMatch ? parseInt(bMatch[1], 10) : 0;
              return aNum - bNum;
            })
            .map((citation) => {
              // Extract the citation number from the ID
              const citationMatch = citation.id?.match(/(\d+)/);
              const citationNumber = citationMatch
                ? parseInt(citationMatch[1], 10)
                : 0;

              return (
                <CitationItem
                  key={`${responseId}-${modelname}-${citation.id}`}
                  id={`citation-${responseId}-${modelname}-${citationNumber}`}
                >
                  <CitationHeader>
                    <CitationId>{citationNumber}</CitationId>
                    <CitationSource>{citation.source}</CitationSource>
                  </CitationHeader>
                  <CitationContent>{citation.content}</CitationContent>
                  {citation.reason && (
                    <CitationMetadata>{citation.reason}</CitationMetadata>
                  )}
                </CitationItem>
              );
            })}
        </>
      )}
    </CitationsContainer>
  );
};

export default Citations;
